'use client';
import { useRef, useMemo, useState } from 'react';
import { useFrame, ThreeEvent } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import type { Stock3D } from '@/lib/scatter3d-geometry';
import { SignalRing } from './SignalRing';
import { compositeScore } from '@/lib/composite-score';
import { compositeTier } from '@/lib/composite-tiers';

export type StockPointSignal =
  | 'enteredAlpha'
  | 'exitedAlpha'
  | null;

export interface StockPointProps {
  stock: Stock3D;
  isSelected: boolean;
  isHighlighted: boolean;
  onClick: () => void;
  signal?: StockPointSignal;
  dimmed?: boolean;
  isAlphaZone?: boolean;
  isSectorTop?: boolean;
}

/**
 * Renders a single stock as a planet-style sphere with optional atmosphere,
 * Saturn ring (when selected), alpha-zone glow, signal rings, label, and tooltip.
 * Animated: smooth lerp to target position + pulse on highlighted (searched) stocks.
 */
export function StockPoint({
  stock, isSelected, isHighlighted, onClick, signal, dimmed, isAlphaZone, isSectorTop,
}: StockPointProps) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Mesh>(null);
  const groupRef = useRef<THREE.Group>(null);

  const color = useMemo(() => new THREE.Color(stock.color), [stock.color]);
  const emissive = useMemo(() => new THREE.Color(stock.color).multiplyScalar(0.15), [stock.color]);
  const atmosColor = useMemo(() => new THREE.Color(stock.color).multiplyScalar(0.4), [stock.color]);

  useFrame(({ clock }) => {
    if (groupRef.current) {
      const pos = groupRef.current.position;
      const dx = stock.x - pos.x;
      const dy = stock.y - pos.y;
      const dz = stock.z - pos.z;
      if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001 || Math.abs(dz) > 0.001) {
        const lerp = 0.08;
        pos.x += dx * lerp;
        pos.y += dy * lerp;
        pos.z += dz * lerp;
      }
    }
    if (isHighlighted && meshRef.current) {
      // Subtle pulse around 1.0 — visual cue without size distortion.
      const pulse = 1.0 + Math.sin(clock.elapsedTime * 4) * 0.05;
      meshRef.current.scale.setScalar(pulse);
    } else if (isSectorTop && meshRef.current) {
      // Bigger, slower pulse for "top 5 in current filter".
      const pulse = 1.0 + Math.sin(clock.elapsedTime * 3) * 0.18;
      meshRef.current.scale.setScalar(pulse);
    }
  });

  const scale = dimmed ? 0.3 : isSelected ? 1.4 : hovered ? 1.15 : 1;
  const r = stock.radius * scale;

  return (
    <group ref={groupRef} position={[stock.x, stock.y, stock.z]}>
      <mesh
        ref={meshRef}
        onClick={(e: ThreeEvent<MouseEvent>) => { e.stopPropagation(); onClick(); }}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHovered(false); document.body.style.cursor = 'auto'; }}
      >
        <sphereGeometry args={[r, 32, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={emissive}
          emissiveIntensity={dimmed ? 0 : isSelected ? 2 : isHighlighted ? 3 : 0.4}
          transparent={dimmed}
          opacity={dimmed ? 0.03 : 1}
          roughness={0.35}
          metalness={0.6}
        />
      </mesh>

      {!dimmed && (
        <mesh>
          <sphereGeometry args={[r * 1.25, 32, 32]} />
          <meshBasicMaterial
            color={atmosColor}
            transparent
            opacity={isSelected ? 0.2 : hovered ? 0.15 : 0.08}
            side={THREE.BackSide}
            depthWrite={false}
          />
        </mesh>
      )}

      {isSelected && (
        <mesh rotation={[Math.PI * 0.4, 0.3, 0]}>
          <ringGeometry args={[r * 1.6, r * 2.2, 48]} />
          <meshBasicMaterial color={stock.color} transparent opacity={0.35} side={THREE.DoubleSide} />
        </mesh>
      )}

      {!dimmed && isAlphaZone && !isSelected && (
        <mesh>
          <sphereGeometry args={[r * 2.2, 16, 16]} />
          <meshBasicMaterial color="#22c55e" transparent opacity={0.08} depthWrite={false} />
        </mesh>
      )}

      {!dimmed && signal === 'enteredAlpha' && <SignalRing radius={r} color="#22c55e" mode="pulse" />}
      {!dimmed && signal === 'exitedAlpha' && <SignalRing radius={r} color="#dc2626" mode="flash" />}
      {!dimmed && isSectorTop && !signal && <SignalRing radius={r} color="#fbbf24" mode="pulse" />}

      {!dimmed && <Html
        position={[0, stock.radius * (isHighlighted || isSectorTop ? 3.5 : isSelected ? 2.5 : 1.8), 0]}
        center
        style={{ pointerEvents: 'none', whiteSpace: 'nowrap' }}
      >
        <div style={{ textAlign: 'center', textShadow: '0 0 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.7)' }}>
          <div style={{
            color: isSectorTop && !isHighlighted ? '#fbbf24' : stock.color,
            fontSize: isHighlighted || isSelected || isSectorTop ? 13 : 8,
            fontFamily: 'monospace',
            fontWeight: 700,
            opacity: 1,
          }}>
            {stock.symbol}
          </div>
          {(isHighlighted || isSelected || isSectorTop) && (
            <div style={{
              color: '#94a3b8',
              fontSize: 9,
              fontFamily: 'system-ui, sans-serif',
              marginTop: 1,
            }}>
              {stock.name}
            </div>
          )}
        </div>
      </Html>}

      {!dimmed && !isHighlighted && (hovered || isSelected) && (() => {
        const composite = compositeScore(stock.quality, stock.valuation);
        const tier = composite != null ? compositeTier(composite) : null;
        // First word of the company name — usually the brand (e.g. "Visa Inc." → "Visa").
        const firstWord = (stock.name ?? '').split(/\s+/)[0] ?? '';
        return (
          <Html
            position={[0, 0, 0]}
            center
            style={{ pointerEvents: 'none', whiteSpace: 'nowrap' }}
          >
            <div style={{ transform: 'translateY(64px)' }}>
              <div style={{
                background: 'rgba(10, 12, 16, 0.92)',
                border: `1px solid ${stock.color}50`,
                borderRadius: 6,
                padding: '5px 10px',
                color: '#e2e8f0',
                fontSize: 11,
                fontFamily: 'monospace',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ color: stock.color, fontWeight: 700 }}>{stock.symbol}</span>
                  {firstWord && firstWord !== stock.symbol && (
                    <span style={{ color: '#cbd5e1', fontWeight: 500 }}>{firstWord}</span>
                  )}
                  {composite != null && tier && (
                    <span style={{ color: tier.hex, fontWeight: 600, marginLeft: 'auto' }}>
                      C {composite.toFixed(2)} <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>{tier.label}</span>
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                  Q: {stock.quality >= 0 ? '+' : ''}{stock.quality.toFixed(2)}σ &middot;
                  V: {stock.valuation >= 0 ? '+' : ''}{stock.valuation.toFixed(2)}σ &middot;
                  M: {stock.technical >= 0 ? '+' : ''}{stock.technical.toFixed(2)}σ
                </div>
              </div>
            </div>
          </Html>
        );
      })()}
    </group>
  );
}
